import React, { useEffect, useState } from "react";

import "./Login.scss";
import { Flex, Button, TextField, Link } from "@aws-amplify/ui-react";
import Logo from "../../../images/pfs-logo.png";
import { Modal } from "antd";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../store/app.store.tsx";
import { setShowAccountCreateConfirmation } from "../../../store/modal.reducer.tsx";
import { UI_STRINGS } from "../../../constants/ui-strings.ts";

export interface Credentials {
  email: string;
  password: string;
}

export interface LoginProps {
  onAuthLogin: () => void;
  onRegister: () => void;
}

export interface IAccountDetails {
  active: boolean;
  first_name: string;
  last_name: string;
  email_address: string;
  middle_name: string;
  userId: number;
  registered: boolean;
}

export const Login = (props: LoginProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [showRegistrationScreen, setShowRegistrationScreen] =
    useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [validatingEmail, setValidatingEmail] = useState<boolean>(false);
  const [accountDetails, setAccountDetails] = useState<IAccountDetails | null>(
    null
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [settingPassword, setSettingPassword] = useState<boolean>(false);
  const [showRegistrationConfirmation, setShowRegistrationConfirmation] =
    useState<boolean>(false);
  const [inactiveError, setInactiveError] = useState<boolean>(() => {
    const flag = localStorage.getItem("pfs_inactive_error");
    if (flag) {
      localStorage.removeItem("pfs_inactive_error");
      return true;
    }
    return false;
  });

  const clearState = () => {
    setShowRegistrationScreen(false);
    setEmailError("");
    setAccountDetails(null);
    setInactiveError(false);
  };

  useEffect(() => {
    const emailRegex: any = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email.trim()) {
        if (!emailRegex.test(email)) {
          setEmailError(UI_STRINGS.LOGIN.EMAIL_VALIDATION_MESSAGE);
          return;
        }
    }
    setEmailError("");
  }, [email]);

  const registerAccount = async () => {
    setAccountDetails(null);
    setValidatingEmail(true);
    setValidationError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PFS_BASE_URL}/accounts`,
        {
          method: "POST",
          headers: {
            "x-api-key": process.env.REACT_APP_API_KEY as string,
          },
          body: JSON.stringify({ email_address: email }),
        }
      );
      const jsonResponse = await response.json();

      if (!response.ok) {
        setValidationError(
          response.status >= 500
            ? UI_STRINGS.SUPPORT.UNEXPECTED_ERROR_MESSAGE
            : jsonResponse.message
        );
      } else {
        if (jsonResponse.active || jsonResponse.registered) {
          setAccountDetails(jsonResponse);
        } else {
          setValidationError(UI_STRINGS.LOGIN.NO_ACTIVE_ACCOUNT);
        }
      }
    } catch (error) {
      setValidationError(UI_STRINGS.SUPPORT.UNEXPECTED_ERROR_MESSAGE);
    } finally {
      setValidatingEmail(false);
    }
  };

  const checkAccountRegistrationStatus = async (jobId: string) => {
    console.log("Checking registration status for job with ID", jobId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_PFS_BASE_URL}/accounts/register/${jobId}`,
        {
          method: "GET",
          headers: {
            "x-api-key": process.env.REACT_APP_API_KEY as string,
          },
        }
      );

      if (!response.ok) {
        setSettingPassword(false);
        setPasswordError(UI_STRINGS.SUPPORT.UNEXPECTED_ERROR_MESSAGE);
        return;
      }

      const responseJson = await response.json();
      if (["FAILED", "SUCCESS"].includes(responseJson.status)) {
        if (responseJson.status === "SUCCESS") {
          clearState();
          setSettingPassword(false);
          dispatch(setShowAccountCreateConfirmation(true));
        } else {
          setSettingPassword(false);
          setPasswordError(responseJson.message);
        }
      } else {
        return checkAccountRegistrationStatus(jobId);
      }
    } catch (error) {
      setSettingPassword(false);
      setPasswordError(UI_STRINGS.SUPPORT.UNEXPECTED_ERROR_MESSAGE);
    }
  };

  const setAccountPassword = async () => {
    setSettingPassword(true);
    setPasswordError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PFS_BASE_URL}/accounts/register`,
        {
          method: "POST",
          headers: {
            "x-api-key": process.env.REACT_APP_API_KEY as string,
          },
          body: JSON.stringify({ ...accountDetails, password }),
        }
      );

      if (!response.ok) {
        setSettingPassword(false);
        setPasswordError(
          response.status >= 500
            ? UI_STRINGS.SUPPORT.UNEXPECTED_ERROR_MESSAGE
            : UI_STRINGS.LOGIN.NO_ACCOUNT_ERROR
        );
      } else {
        const responseJson = await response.json();
        checkAccountRegistrationStatus(responseJson.job_id);
      }
    } catch (error) {
      setSettingPassword(false);
      setPasswordError(UI_STRINGS.SUPPORT.UNEXPECTED_ERROR_MESSAGE);
    }
  };

  return (
    <div className="login-container">
      <div className="login-wrapper">
        <div className="logo-wrapper">
          <img src={Logo} alt="logo" />
        </div>
        <span className="page-title">{UI_STRINGS.LOGIN.TITLE}</span>
        {!showRegistrationScreen ? (
          <div className="login-form">
            {inactiveError && (
              <div className="registration mat-card">
                <div className="error">
                  <p>{UI_STRINGS.LOGIN.INACTIVE_SIGN_IN_TITLE}</p>
                  <br />
                  <br />
                  <p>
                    Contact the national program office via email (
                    <a href={`mailto:${UI_STRINGS.SECURITY.SUPPORT_EMAIL}`} className="link">
                      {UI_STRINGS.SECURITY.SUPPORT_EMAIL}
                    </a>
                    ) for further assistance. Include your name, email address, and facility affiliation.
                  </p>
                </div>
              </div>
            )}
            <Flex className="vertical-padded">
              <Button
                variation="primary"
                isFullWidth
                size="large"
                backgroundColor="#8CC63F"
                onClick={() => props.onAuthLogin()}
              >
                {UI_STRINGS.LOGIN.SIGN_IN_BUTTON}
              </Button>
            </Flex>
            <Flex className="vertical-padded">
              <Button
                variation="primary"
                isFullWidth
                size="large"
                backgroundColor="#8CC63F"
                onClick={() => setShowRegistrationScreen(true)}
              >
                {UI_STRINGS.LOGIN.REGISTER_BUTTON}
              </Button>
            </Flex>
          </div>
        ) : null}

        {showRegistrationScreen && !accountDetails ? (
          <div className="registration mat-card">
            {validationError && <div className="error" dangerouslySetInnerHTML={{ __html: validationError }}></div>}
            <p>{UI_STRINGS.LOGIN.EMAIL_INSTRUCTION}</p>
            <TextField
              placeholder={UI_STRINGS.LOGIN.EMAIL_PLACEHOLDER}
              label={UI_STRINGS.LOGIN.EMAIL_LABEL}
              type="email"
              hasError={emailError.trim() !== ""}
              disabled={validatingEmail}
              name="Email Address"
              size="large"
              value={email}
              errorMessage={emailError}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button
              loadingText="Validating email address..."
              onClick={registerAccount}
              isDisabled={emailError.trim() !== ""}
              isLoading={validatingEmail}
              color="white"
              backgroundColor="rgb(140, 198, 63)"
            >
              Register
            </Button>

            <p className="text-centered">
              {UI_STRINGS.LOGIN.ALREADY_REGISTERED}{" "}
              <Link
                onClick={() => {
                  setEmail("");
                  setEmailError("");
                  setValidationError("");
                  setShowRegistrationScreen(false);
                }}
              >
                {UI_STRINGS.LOGIN.SIGN_IN_BUTTON}
              </Link>
            </p>
          </div>
        ) : null}

        {showRegistrationScreen && !validationError && accountDetails ? (
          <div className="registration details">
            {!accountDetails.registered && accountDetails.active ? (
              <div className="account-details">
                <div className="details-item">
                  <span className="title">First Name:</span>
                  <span className="value">{accountDetails.first_name}</span>
                </div>
                <div className="details-item">
                  <span className="title">Last Name:</span>
                  <span className="value">{accountDetails.last_name}</span>
                </div>
                <div className="details-item">
                  <span className="title">Email:</span>
                  <span className="value">{accountDetails.email_address}</span>
                </div>
                <div className="details-item flex-col">
                  <TextField
                    placeholder="Password"
                    label="Password"
                    type="password"
                    hasError={passwordError.trim() !== ""}
                    disabled={settingPassword}
                    name="Password"
                    className="password-field"
                    size="large"
                    value={password}
                    errorMessage={passwordError}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <Button
                    loadingText="Setting Password..."
                    onClick={setAccountPassword}
                    isDisabled={password.trim() === ""}
                    isLoading={settingPassword}
                    backgroundColor="#8CC63F"
                    variation="primary"
                    isFullWidth
                  >
                    Set Password
                  </Button>
                </div>
              </div>
            ) : null}

            {accountDetails.registered ? (
              <div className="account-details mat-card">
                <p>{UI_STRINGS.LOGIN.ACCOUNT_ALREADY_REGISTERED}</p>
                <div className="btn-group">
                  <Flex className="vertical-padded">
                    <Button
                      variation="primary"
                      isFullWidth
                      size="large"
                      backgroundColor="#8CC63F"
                      onClick={() => props.onAuthLogin()}
                    >
                      {UI_STRINGS.LOGIN.SIGN_IN_BUTTON}
                    </Button>
                  </Flex>

                  <Flex className="vertical-padded">
                    <Button
                      variation="primary"
                      isFullWidth
                      size="large"
                      backgroundColor="#8CC63F"
                      onClick={() => {
                        setEmail("");
                        setAccountDetails(null);
                        setShowRegistrationScreen(true);
                      }}
                    >
                      {UI_STRINGS.LOGIN.REGISTER_ANOTHER_ACCOUNT}
                    </Button>
                  </Flex>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <Modal
        title={UI_STRINGS.LOGIN.CONGRATULATIONS_TITLE}
        open={showRegistrationConfirmation}
        onOk={() => {
          setShowRegistrationConfirmation(false);
          navigate(0);
        }}
        okText={UI_STRINGS.LOGIN.OK_BUTTON}
      >
        <p>
          {UI_STRINGS.LOGIN.CONGRATULATIONS_MESSAGE}
        </p>
      </Modal>

      <div className="security-message">
        <p>
          {UI_STRINGS.SECURITY.UNAUTHORIZED_ACCESS}
        </p>

        <p>
          If you are a member of a Local Safety Council and need an account on
          the Partnership For Safety Data portal, please contact your Air
          Traffic Manager or NATCA Facility rep and he/she will initiate the
          process to generate an account for you. You may also contact the
          national program office via email (
          <a href={`mailto:${UI_STRINGS.SECURITY.SUPPORT_EMAIL}`} className="link">
            {UI_STRINGS.SECURITY.SUPPORT_EMAIL}
          </a>
          ) for further assistance. Include your name, email address, and
          affiliation and we will assist you.
        </p>

        <p>
          For security reasons, please Log Out and Exit your web browser when
          you are done accessing services that require authentication!
        </p>

        <p>
          For technical support, or help using this system (including password
          resets), please email Facility Safety Member Support at&nbsp;
          <a href={`mailto:${UI_STRINGS.SUPPORT.EMAIL_LINK_TEXT}`} className="link">
            {UI_STRINGS.SUPPORT.EMAIL_LINK_TEXT}
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default Login;
